import React, { useState, useEffect } from 'react';
import AgentLayout from '../../components/layout/AgentLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import {
  DollarSign,
  TrendingUp,
  Download,
  ArrowUpRight,
  ArrowDownRight,
  Wallet,
  CreditCard,
  PiggyBank,
  CheckCircle,
  Clock,
  XCircle,
  RefreshCw,
  FileText,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { agentAPI } from '../../services/api';
import { useRoleBasePath } from '../../hooks/useRoleBasePath';
import { toast } from 'sonner';
import { format } from 'date-fns';

const PAYOUT_STATUS_CONFIG = {
  requested: { label: 'Requested', color: 'bg-amber-100 text-amber-700', icon: Clock },
  processing: { label: 'Processing', color: 'bg-blue-100 text-blue-700', icon: RefreshCw },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-700', icon: XCircle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-700', icon: XCircle },
};

export default function AgentEarnings() {
  const basePath = useRoleBasePath();
  const [loading, setLoading] = useState(true);
  const [earnings, setEarnings] = useState({ total: 0, pending: 0, approved: 0, paid: 0 });
  const [wallet, setWallet] = useState({
    availableBalance: 0, pendingBalance: 0, lifetimeEarnings: 0,
    payoutThreshold: 100, isPayoutEligible: false, totalCommissions: 0
  });
  const [monthlyBreakdown, setMonthlyBreakdown] = useState([]);
  const [byStudent, setByStudent] = useState([]);
  const [byServiceType, setByServiceType] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [payoutDialog, setPayoutDialog] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutNote, setPayoutNote] = useState('');
  const [payoutLoading, setPayoutLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [earningsRes, payoutsRes] = await Promise.all([
        agentAPI.getEarnings(),
        agentAPI.getPayouts({ limit: 10 })
      ]);

      setEarnings(earningsRes.earnings || {});
      setWallet(earningsRes.wallet || {});
      setMonthlyBreakdown(earningsRes.monthlyBreakdown || []);
      setByStudent(earningsRes.byStudent || []);
      setByServiceType(earningsRes.byServiceType || []);
      setPayouts(payoutsRes.payouts || []);
    } catch (error) {
      console.error('Failed to fetch earnings:', error);
      toast.error('Failed to load earnings data');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await agentAPI.exportCommissions({});
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `earnings-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Earnings report downloaded');
    } catch (error) {
      toast.error('Failed to export earnings');
    }
  };

  const handleRequestPayout = async () => {
    try {
      setPayoutLoading(true);
      const amount = parseFloat(payoutAmount) || wallet.availableBalance;
      await agentAPI.requestPayout(amount, payoutNote);
      toast.success('Payout request submitted successfully');
      setPayoutDialog(false);
      setPayoutAmount('');
      setPayoutNote('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to submit payout request');
    } finally {
      setPayoutLoading(false);
    }
  };

  const totalReferrals = wallet.totalCommissions || 0;
  const avgPerReferral = totalReferrals > 0 ? Math.round(earnings.total / totalReferrals) : 0;

  if (loading) {
    return (
      <AgentLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout>
      <div className="space-y-6" data-testid="agent-earnings">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-outfit font-bold text-gray-900">Earnings</h1>
            <p className="text-gray-500">Your complete earnings overview</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExport} data-testid="download-earnings-btn">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
            <Button
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="request-payout-btn"
              onClick={() => {
                setPayoutAmount(String(wallet.availableBalance));
                setPayoutDialog(true);
              }}
              disabled={!wallet.isPayoutEligible}
            >
              <Wallet className="w-4 h-4 mr-2" />
              Request Payout
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="p-5 bg-gradient-to-br from-emerald-500 to-teal-500 text-white border-0 shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-sm mb-1">Total Earnings</p>
                  <p className="text-3xl font-bold">${(earnings.paid || 0).toLocaleString()}</p>
                </div>
                <DollarSign className="w-8 h-8 text-emerald-200" />
              </div>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="p-5 bg-white border-0 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Available Balance</p>
                  <p className="text-2xl font-bold text-gray-900">${(wallet.availableBalance || 0).toLocaleString()}</p>
                  {wallet.isPayoutEligible && (
                    <p className="text-xs text-emerald-600 mt-1">Eligible for withdrawal</p>
                  )}
                </div>
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-blue-600" />
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card className="p-5 bg-white border-0 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Total Referrals</p>
                  <p className="text-2xl font-bold text-gray-900">{totalReferrals}</p>
                </div>
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <Card className="p-5 bg-white border-0 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Avg per Referral</p>
                  <p className="text-2xl font-bold text-gray-900">${avgPerReferral.toLocaleString()}</p>
                </div>
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <PiggyBank className="w-5 h-5 text-amber-600" />
                </div>
              </div>
            </Card>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Monthly Breakdown */}
          <Card className="p-6 bg-white border-0 shadow-sm">
            <h3 className="text-lg font-outfit font-bold text-gray-900 mb-4">Monthly Breakdown</h3>

            {monthlyBreakdown.length === 0 ? (
              <div className="text-center py-8">
                <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No monthly data yet</p>
                <p className="text-sm text-gray-400 mt-1">Earnings will appear here as commissions are created</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="monthly-breakdown">
                {monthlyBreakdown.map((month, index) => {
                  const isPositive = month.growth && !month.growth.startsWith('-');
                  return (
                    <motion.div
                      key={month.month}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-gray-900">{month.month}</p>
                        <p className="text-sm text-gray-500">{month.referrals} referrals</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">${month.amount.toLocaleString()}</p>
                        <div className={`flex items-center gap-1 ${isPositive ? 'text-green-600' : 'text-red-500'}`}>
                          {isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                          <span className="text-xs">{month.growth}</span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Payout History */}
          <Card className="p-6 bg-white border-0 shadow-sm">
            <h3 className="text-lg font-outfit font-bold text-gray-900 mb-4">Payout History</h3>

            {payouts.length === 0 ? (
              <div className="text-center py-8">
                <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No payouts yet</p>
                <p className="text-sm text-gray-400 mt-1">Request a withdrawal when you have approved earnings</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="payout-history">
                {payouts.map((payout, index) => {
                  const statusCfg = PAYOUT_STATUS_CONFIG[payout.status] || PAYOUT_STATUS_CONFIG.requested;
                  const StatusIcon = statusCfg.icon;

                  return (
                    <motion.div
                      key={payout.payoutId}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center justify-between p-4 border border-gray-100 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          payout.status === 'completed' ? 'bg-green-100' : 'bg-gray-100'
                        }`}>
                          <CreditCard className={`w-5 h-5 ${
                            payout.status === 'completed' ? 'text-green-600' : 'text-gray-500'
                          }`} />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">${payout.amount.toLocaleString()}</p>
                          <p className="text-sm text-gray-500">{payout.payoutMethod?.replace(/_/g, ' ') || 'Bank Transfer'}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={statusCfg.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusCfg.label}
                        </Badge>
                        <p className="text-xs text-gray-400 mt-1">
                          {payout.processedAt
                            ? format(new Date(payout.processedAt), 'MMM d, yyyy')
                            : format(new Date(payout.requestedAt || payout.createdAt), 'MMM d, yyyy')}
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}

            {payouts.length > 0 && (
              <Button variant="outline" className="w-full mt-4" onClick={() => window.location.href = `${basePath}/commission`}>
                View All in Commission
              </Button>
            )}
          </Card>
        </div>

        {/* Top Students & Service Breakdown */}
        {(byStudent.length > 0 || byServiceType.length > 0) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {byStudent.length > 0 && (
              <Card className="p-6 bg-white border-0 shadow-sm">
                <h3 className="text-lg font-outfit font-bold text-gray-900 mb-4">Top Students by Earnings</h3>
                <div className="space-y-3">
                  {byStudent.map((student, index) => (
                    <div key={student.studentId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-gray-400 w-5">{index + 1}.</span>
                        <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center text-white font-semibold text-xs">
                          {student.name?.split(' ').map(n => n[0]).join('') || '?'}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{student.name}</p>
                          <p className="text-xs text-gray-500">{student.count} commissions</p>
                        </div>
                      </div>
                      <p className="font-semibold text-gray-900">${student.total.toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {byServiceType.length > 0 && (
              <Card className="p-6 bg-white border-0 shadow-sm">
                <h3 className="text-lg font-outfit font-bold text-gray-900 mb-4">Earnings by Service Type</h3>
                <div className="space-y-4">
                  {byServiceType.map((svc) => (
                    <div key={svc.serviceType}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700">
                          {svc.serviceType.replace(/_/g, ' ')}
                        </span>
                        <span className="text-sm font-semibold text-gray-900">${svc.total.toLocaleString()}</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full"
                          style={{ width: `${svc.percentage}%` }}
                        />
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-xs text-gray-400">{svc.count} commissions</span>
                        <span className="text-xs text-gray-400">{svc.percentage}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Payout Request Dialog */}
        <Dialog open={payoutDialog} onOpenChange={setPayoutDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Request Payout</DialogTitle>
              <DialogDescription>
                Your available balance is ${wallet.availableBalance.toLocaleString()}.
                Minimum withdrawal is ${wallet.payoutThreshold}.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Amount ($)</label>
                <Input
                  type="number"
                  value={payoutAmount}
                  onChange={(e) => setPayoutAmount(e.target.value)}
                  placeholder={`Max: ${wallet.availableBalance}`}
                  min={wallet.payoutThreshold}
                  max={wallet.availableBalance}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Note (optional)</label>
                <Input
                  value={payoutNote}
                  onChange={(e) => setPayoutNote(e.target.value)}
                  placeholder="Any special instructions..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPayoutDialog(false)}>Cancel</Button>
              <Button
                onClick={handleRequestPayout}
                disabled={payoutLoading || !payoutAmount || parseFloat(payoutAmount) < wallet.payoutThreshold}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                {payoutLoading ? 'Processing...' : 'Submit Request'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AgentLayout>
  );
}
